import { useAuth } from '../../contexts/auth/useAuth';
import { Box, TextField, CircularProgress } from '@mui/material';
import { useMemo } from 'react';
import { AccessibleAutocomplete } from '../inputs/AccessibleAutocomplete';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { useUsers } from '../../hooks/api/useUsers';

const NavbarAccountSelector = () => {
  const { accounts: accountList, isLoadingAccount: isLoading } = useUsers();
  const navigate = useNavigate();
  const { account, setAccount } = useAuthContext();
  const { isAuthenticated } = useAuth();

  const handleAccountChange = (newAccountId: string | null) => {
    if (newAccountId) {
      setAccount(newAccountId);
    }
    // Navigate away from specific agreement pages when switching accounts
    if (window.location.pathname.includes('/agreements/')) {
      navigate('/agreements');
    }
  };

  const options = useMemo(() => {
    if (!accountList || Object.keys(accountList).length === 0) return [];

    return Object.entries(accountList as Record<string, string>)
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, id]) => ({
        label: `${name} - ${id}`,
        id,
      }));
  }, [accountList]);

  const selectedOption = useMemo(
    () => options.find((option) => option.id === account) || null,
    [account, options]
  );

  if (!isAuthenticated || isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 280 }}>
        {isLoading && <CircularProgress size={20} />}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: { xs: 140, md: 280 },
      }}
    >
      <AccessibleAutocomplete
        value={selectedOption}
        disablePortal
        clearOnEscape
        options={options}
        onChange={(_, newValue) => handleAccountChange(newValue?.id || null)}
        sx={{ width: '100%' }}
        aria-label="Account selection dropdown"
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Account"
            aria-label="Account selector"
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      />
    </Box>
  );
};

export default NavbarAccountSelector;
